import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, X } from "lucide-react";
import { useApp } from "@/context/AppContext";

function fmt(ms) {
  if (ms <= 0) return "00:00";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Small sticky widget showing the countdown on the soonest-expiring active offer.
export function ExpiryCountdown() {
  const navigate = useNavigate();
  const { profile } = useApp();
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(null); // code of dismissed offer

  const active = useMemo(() => {
    const offers = (profile?.offers || []).filter((o) => !o.redeemed && o.expiresAt && new Date(o.expiresAt).getTime() > now);
    offers.sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt));
    return offers[0] || null;
  }, [profile, now]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!active || dismissed === active.code) return null;

  const remaining = new Date(active.expiresAt).getTime() - now;
  const urgent = remaining < 60000;

  return (
    <AnimatePresence>
      <motion.button
        key={active.code}
        data-testid="expiry-countdown"
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        onClick={() => navigate(`/offers?code=${active.code}`)}
        className={`fixed z-40 left-1/2 -translate-x-1/2 bottom-28 max-w-md w-[calc(100%-3rem)] rounded-2xl px-4 py-3 flex items-center gap-3 shadow-lg no-tap-highlight ${urgent ? "bg-red-500 text-white" : "bg-slate-800 text-slate-50 border border-amber-400/40"}`}
      >
        <Clock size={18} className={urgent ? "text-white" : "text-amber-400"} strokeWidth={2.5} />
        <div className="flex-1 text-left min-w-0">
          <p className="text-[10px] font-black uppercase tracking-wider opacity-80 truncate">Offer expires · {active.title}</p>
          <p className="font-display font-black text-lg leading-none" data-testid="expiry-countdown-time">{fmt(remaining)}</p>
        </div>
        <span
          data-testid="expiry-countdown-dismiss"
          onClick={(e) => { e.stopPropagation(); setDismissed(active.code); }}
          className="p-1 opacity-70"
        >
          <X size={16} />
        </span>
      </motion.button>
    </AnimatePresence>
  );
}
